// LIGHT COPY of components/topology/TopologyAttackChain.jsx for /event-trace-light. The dark original is untouched;
// changes to it are NOT carried here automatically.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { Controls, Background, BackgroundVariant, ReactFlowProvider, useReactFlow } from 'reactflow';
import 'reactflow/dist/style.css';
import { KNOWN_DEVICES, EDGE_AGENT_IP, EDGE_AGENT_NAME, PROTOCOL_COLORS, fmtUtc, plainPolicyName } from '../../constants/topologyLight';
import { deviceTypeLabel, deviceRoleClass, knownDeviceName } from '../../constants/deviceTypes';
import { eventAction, showEnforcementOff } from '../../utils/monitorMode';
import EnforcementOffLabel from '../common/EnforcementOffLabel';
import { TOPOLOGY_NODE_TYPES } from './TopologyNodesLight';
import { eventSourceLabel, SOURCE_NOT_REPORTED, AGENT_EVENT_SOURCES } from '../../utils/eventSource';
import { edgeNodeDisplayName } from '../../utils/edgeNodeDisplayName';
import { computeCounts, formatHours } from '../../utils/counts';
import { attackStages, STAGE_ORDER } from './attackStageLight';

const SPEED_DELAYS = { '0.5x': 3000, '1x': 1500, '2x': 750, '4x': 300 };

const WINDOW_OPTIONS = [
  { label: '15m', value: 0.25 },
  { label: '1h',  value: 1 },
  { label: '2h',  value: 2 },
  { label: '6h',  value: 6 },
  { label: '24h', value: 24 },
];

// ─── helpers ────────────────────────────────────────────────────────────────

// Normalize protocol strings from the agent (IEC61850_GOOSE → goose, etc.)
function normalizeProtocol(proto) {
  if (!proto) return 'tcp';
  const p = proto.toLowerCase();
  if (p === 'iec61850_goose') return 'goose';
  if (p === 'iec61850_mms')   return 'mms';
  if (p === 'iec61850')       return 'mms';
  return p;
}

// The name the graph gives a device node: the registered asset's name, then the curated KNOWN_DEVICES
// name; null when neither exists. The Operator Summary uses the same function so the two agree.
function deviceNodeName(ip, assetsByIp) {
  if (!ip) return null;
  return (assetsByIp && assetsByIp[ip]?.asset_name) || knownDeviceName(ip, KNOWN_DEVICES[ip]?.name) || null;
}

// The name the step list, step detail and polling groups give an address: the graph's name
// (deviceNodeName: registered asset first, then KNOWN_DEVICES), else the bare IP. One precedence on
// the whole page, so a step never names a device differently from its graph node and the summary.
function nodeName(ip, assetsByIp) {
  if (!ip) return null;
  return deviceNodeName(ip, assetsByIp) || ip;
}

// A step with no source IP may still have a known source: the edge agent's
// own polls/discovery carry provenance in details, and L2 frames a MAC.
// Returns null when the data does not say, so the caller's fallback applies.
function knownSourceLabel(s) {
  const label = eventSourceLabel(s && s.events && s.events[0]);
  return label === SOURCE_NOT_REPORTED ? null : label;
}

function stepLabel(s, assetsByIp) {
  const proto = normalizeProtocol(s.protocol);
  if (!s.sourceIp && !s.destIp && proto === 'goose') {
    return 'GOOSE · L2 Multicast Domain';
  }
  const src = nodeName(s.sourceIp, assetsByIp)
    || knownSourceLabel(s)
    || (s.isPeriodicPoll ? '[Polling]' : '[Internal Host]');
  const dst = nodeName(s.destIp, assetsByIp)
    || (s.destIp ? s.destIp : '[No Dest]');
  return `${src} → ${dst}`;
}

// The step detail's "event details": a curated list of the logged event row's fields. This row is
// the security_events record, not sealed compliance evidence, so nothing here is called evidence,
// and internal keys (capture_interface, agent_version, …) are not shown. The packet fingerprint is
// the agent's SHA-256 of the triggering packet, where the agent sent one.
const EVENT_TYPE_LABELS = {
  PolicyMatchedLog: 'Policy match',
  PolicyMatchedAlert: 'Policy match (alert)',
  AssetDiscovered: 'Asset discovered',
  OTExposureSummary: 'Exposure summary',
  VulnerabilityAlert: 'Vulnerability alert',
  CISAKEVSync: 'CISA KEV sync',
  EnforcementAction: 'Enforcement action',
};
const OBSERVED_VIA_LABELS = { live_traffic_capture: 'Passive capture', ...AGENT_EVENT_SOURCES };
// An unlisted value is still shown, in words: "SomeNewType" -> "Some new type", "new_source" -> "New source".
function humanize(token) {
  const words = String(token).replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
const eventTypeLabel = t => (t ? EVENT_TYPE_LABELS[t] || humanize(t) : null);
const observedViaLabel = v => (v ? OBSERVED_VIA_LABELS[v] || humanize(v) : null);

// The rule's raw condition expression is not shown: it is the policy's text, written for whichever
// site the policy came from (it can name another site's lab IPs); the rule name is shown instead.
function eventDetailFields(ev) {
  if (!ev) return [];
  const d = ev.details || {};
  const te = d.triggering_event || {};
  const port = p => (p !== undefined && p !== null && p !== '' ? `:${p}` : '');
  const src = ev.source_ip || te.source_ip;
  const dst = ev.dest_ip || ev.destination_ip || te.dest_ip;
  const rows = [
    ['Event ID', ev.id],
    ['Logged', ev.timestamp ? fmtUtc(ev.timestamp) : null],
    ['Event type', eventTypeLabel(ev.event_type)],
    ['Policy', ev.policy_name || d.policy_name],
    ['Rule', ev.rule_name || d.rule_name],
    ['Action', ev.action || d.rule_action],
    ['Protocol', ev.protocol || te.protocol],
    ['Source', src ? `${src}${port(te.source_port)}` : null],
    ['Destination', dst ? `${dst}${port(te.dest_port)}` : null],
    ['Observed via', observedViaLabel(d.event_source || te.event_source)],
    ['Packet fingerprint (SHA-256)', te.packet_sha256 || d.packet_sha256],
  ];
  return rows.filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, String(v)]);
}

// ─── periodic poll detection ─────────────────────────────────────────────────
// Marks steps as isPeriodicPoll when: source_ip is null and they arrive at
// regular 2–10 min intervals to the same dest+protocol pair.
// Groups by destIp::protocol so Modbus, DNP3, MMS, and raw TCP are all caught.
// The agent DPI layer emits 'modbus' not 'tcp' for Modbus-over-TCP frames,
// which is why the old '=== tcp' guard missed the entire polling pattern.
function markPeriodicPolls(steps) {
  if (steps.length < 3) return steps;

  const byDestProto = {};
  steps.forEach((s, i) => {
    if (!s.sourceIp && s.destIp) {
      const key = `${s.destIp}::${normalizeProtocol(s.protocol)}`;
      (byDestProto[key] = byDestProto[key] || []).push({ s, i });
    }
  });

  const periodicIdx = new Set();

  Object.values(byDestProto).forEach(group => {
    if (group.length < 3) return;
    const intervals = [];
    for (let k = 1; k < group.length; k++) {
      intervals.push(
        (new Date(group[k].s.timestamp) - new Date(group[k - 1].s.timestamp)) / 1000
      );
    }
    const inRange = intervals.filter(iv => iv >= 120 && iv <= 600);
    if (inRange.length >= Math.ceil(intervals.length * 0.6)) {
      group.forEach(g => periodicIdx.add(g.i));
    }
  });

  return steps.map((s, i) => ({ ...s, isPeriodicPoll: periodicIdx.has(i) }));
}

// ─── event → steps ──────────────────────────────────────────────────────────
function buildAttackSteps(events) {
  if (!events || events.length === 0) return [];
  const sorted = [...events].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const raw = [];
  let cluster = null;

  sorted.forEach(ev => {
    const t = new Date(ev.timestamp).getTime();
    // An event carries dangerous OT intent if flagged as a write/dangerous action
    // in its details — used to escalate cluster severity regardless of cloud severity label.
    const isDangerous = !!(ev.details?.dnp3_is_dangerous_write || ev.details?.dnp3_is_dangerous
      || ev.details?.mms_is_write || ev.details?.bacnet_is_write);

    if (cluster && ev.source_ip === cluster.sourceIp && t - cluster.lastT <= 10000) {
      cluster.events.push(ev);
      cluster.lastT = t;
      if ((ev.severity || '').toLowerCase() === 'critical' || isDangerous) cluster.hasCritical = true;
      if (ev.enforcement_event) cluster.hasEnforcement = true;
      if (ev.policy_name && !cluster.policies.includes(ev.policy_name)) cluster.policies.push(ev.policy_name);
    } else {
      if (cluster) raw.push(cluster);
      cluster = {
        sourceIp: ev.source_ip,
        destIp:   ev.destination_ip || ev.dest_ip,
        protocol: ev.protocol,
        timestamp: ev.timestamp,
        lastT: t,
        events: [ev],
        hasCritical:    (ev.severity || '').toLowerCase() === 'critical' || isDangerous,
        hasEnforcement: !!ev.enforcement_event,
        policies: ev.policy_name ? [ev.policy_name] : [],
      };
    }
  });
  if (cluster) raw.push(cluster);

  // Drop steps with no resolved endpoints and no security signal.
  // Protocol-agnostic: catches TCP, HTTPS, null-protocol agent traffic alike.
  // A step is noise if: no source, no dest, no policy match, not critical, no enforcement.
  const signal = raw.filter(s => {
    if (s.sourceIp || s.destIp) return true;       // at least one endpoint known — keep
    if (s.hasCritical || s.hasEnforcement) return true;
    if (s.policies.length > 0) return true;
    return false;  // both endpoints unresolvable, no signal → drop
  });

  const numbered = signal.map((s, i) => ({ stepNum: i + 1, ...s }));

  // Mark the first step of each incident gap (> 15 min between consecutive steps)
  for (let i = 1; i < numbered.length; i++) {
    const gap = (new Date(numbered[i].timestamp) - new Date(numbered[i - 1].timestamp)) / 60000;
    if (gap > 15) numbered[i].incidentBreak = true;
  }

  return markPeriodicPolls(numbered);
}

// ─── semantic zone layout ────────────────────────────────────────────────────
// Fixed positions by IP so attack arrows span the canvas meaningfully.
// Four zones: substation (top-left), external threats (top-right),
// field/process (bottom-left), SCADA/HMI (bottom-right), edge agent (center).
const ZONE_POSITIONS = {
  // Substation protection zone — top-left
  '192.168.1.100': { x: 20,  y: 80  },
  '192.168.1.101': { x: 20,  y: 180 },
  '192.168.1.102': { x: 20,  y: 280 },
  '192.168.1.103': { x: 20,  y: 380 },
  '192.168.1.104': { x: 20,  y: 480 },
  '192.168.1.105': { x: 175, y: 480 },
  // Field / process zone — bottom-left
  '192.168.1.70':  { x: 120, y: 610 },   // Allen-Bradley PLC — primary target
  '192.168.1.71':  { x: 280, y: 610 },
  '192.168.1.75':  { x: 120, y: 710 },
  // SCADA / HMI zone — bottom-right
  '192.168.1.72':  { x: 660, y: 460 },
  '192.168.1.73':  { x: 660, y: 560 },
  '192.168.1.74':  { x: 660, y: 660 },
  // Edge agent — center
  [EDGE_AGENT_IP]: { x: 345, y: 330 },
};

// Unknown/external IPs stack in the top-right zone, two columns
function externalPosition(idx) {
  return { x: 520 + (idx % 2) * 165, y: 80 + Math.floor(idx / 2) * 115 };
}

// Fleet-mode grid layout: place assets in rows below the edge agent (x=345, y=330)
function fleetAssetPosition(allFleetIps, ip) {
  const COLS = Math.min(allFleetIps.length, 4);
  const COL_GAP = 160;
  const idx = allFleetIps.indexOf(ip);
  if (idx === -1) return externalPosition(0);
  const row = Math.floor(idx / COLS);
  const col = idx % COLS;
  const rowCount = Math.min(allFleetIps.length - row * COLS, COLS);
  const rowStartX = Math.max(0, 345 - ((rowCount - 1) * COL_GAP) / 2);
  return { x: rowStartX + col * COL_GAP, y: 490 + row * 170 };
}

const ZONE_LABELS = [
  { id: 'zlabel-sub',      label: 'SUBSTATION',       color: '#3B6EA5', x: 22,  y: 48  },
  { id: 'zlabel-ext',      label: 'EXTERNAL SOURCES', color: '#A53B3B', x: 520, y: 48  },
  { id: 'zlabel-field',    label: 'FIELD / PROCESS',  color: '#3B7A3B', x: 22,  y: 578 },
  { id: 'zlabel-scada',    label: 'SCADA / HMI',      color: '#4B5B9A', x: 660, y: 428 },
];

// Declared non-OT infrastructure IPs — gateway, agent host, etc.
// These are legitimate RFC-1918 IPs that should never be labeled Rogue Internal.
const EXCLUDED_OT_IPS = new Set([
  '10.10.1.1', '10.20.1.1', '10.30.1.1',  // substation site gateways
  '192.168.1.1',                            // home/lab LAN gateway
]);

// OT address scope: 192.168.1.60-120 for Pi/local-lab; any 10.x.x.x for fleet sites.
function isInOtScope(ip, assetsByIp) {
  if (assetsByIp && assetsByIp[ip]) return true;  // registered asset = always in scope
  if (ip.startsWith('10.')) return true;           // fleet subnets are OT networks
  if (!ip.startsWith('192.168.1.')) return false;
  const last = parseInt(ip.split('.').pop(), 10);
  return last >= 60 && last <= 120;
}

// Infer a role label for IPs not in KNOWN_DEVICES.
// Three states for LAN IPs: Rogue Internal (OT scope + violation), Uninventoried OT (OT scope, no violation),
// Out-of-Scope (outside OT range — gateway, PCs, etc. — never labeled Rogue just for being unknown).
function inferExternalRole(ip, steps, assetsByIp) {
  // Declared infra IPs (gateways etc.) — Out-of-Scope before any other check
  if (EXCLUDED_OT_IPS.has(ip)) return 'Out-of-Scope';

  const isLan = ip.startsWith('192.168.') || ip.startsWith('10.') ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(ip);

  if (!isLan) {
    const asSource = steps.some(s => s.sourceIp === ip);
    const asDest   = steps.some(s => s.destIp   === ip);
    const hasBlock = steps.some(s => s.sourceIp === ip && s.hasEnforcement);
    if (hasBlock) return 'Candidate Origin';
    if (asSource && !asDest) return 'Scanner';
    return 'External Host';
  }

  const hasViolation = steps.some(s =>
    (s.sourceIp === ip || s.destIp === ip) && (s.hasCritical || s.hasEnforcement)
  );
  if (isInOtScope(ip, assetsByIp)) {
    return hasViolation ? 'Rogue Internal' : 'Uninventoried OT';
  }
  return 'Out-of-Scope';
}

// ─── steps → ReactFlow ───────────────────────────────────────────────────────
function buildAttackGraph(steps, currentStep, edgeNode, assetsByIp) {
  const nodes = [];
  const edges = [];

  const activeStep = steps?.[currentStep] || null;

  // Collect IPs involved up to current step
  const involvedIps = new Set();
  (steps || []).slice(0, currentStep + 1).forEach(s => {
    if (s.sourceIp) involvedIps.add(s.sourceIp);
    if (s.destIp)   involvedIps.add(s.destIp);
  });
  involvedIps.add(EDGE_AGENT_IP);

  // Separate involved known-zone IPs from external/unknown
  const ipList = Array.from(involvedIps);
  const externalIps = ipList.filter(ip => ip !== EDGE_AGENT_IP && !ZONE_POSITIONS[ip]);
  let extIdx = 0;

  const hasFleetAssets = assetsByIp && Object.keys(assetsByIp).length > 0;
  const allFleetIps = hasFleetAssets ? Object.keys(assetsByIp) : [];

  // Zone label nodes — skip in fleet mode (labels are Pi/local-lab specific)
  if (!hasFleetAssets) {
    ZONE_LABELS.forEach(z => {
      nodes.push({
        id: z.id, type: 'zoneLabel',
        position: { x: z.x, y: z.y },
        data: { label: z.label, color: z.color },
        selectable: false, draggable: false,
      });
    });
  }

  // Render dimmed background nodes to fill zones.
  // Fleet sites use assets table; Pi/local-lab falls back to KNOWN_DEVICES.
  if (hasFleetAssets) {
    // Fleet mode: render registered assets that aren't yet involved as dimmed background
    Object.values(assetsByIp).forEach(asset => {
      const ip = asset.ip_address;
      if (involvedIps.has(ip)) return;
      const pos = ZONE_POSITIONS[ip] || fleetAssetPosition(allFleetIps, ip);
      nodes.push({
        id: `node-${ip}`, type: 'otDevice', position: pos,
        data: {
          ip,
          name: asset.asset_name || ip,
          role: deviceTypeLabel(asset.asset_name, asset.vendor, asset.asset_type || 'Device'),
          roleClass: deviceRoleClass(asset.asset_name, asset.vendor, asset.asset_type || 'Device'),
          riskLevel: 'normal', protocols: [], isInactive: true,
        },
      });
    });
  } else {
    // Pi/local-lab mode: render all 12 KNOWN_DEVICES dimmed
    Object.entries(KNOWN_DEVICES).forEach(([ip, kd]) => {
      if (involvedIps.has(ip)) return;
      const pos = ZONE_POSITIONS[ip];
      if (!pos) return;
      nodes.push({
        id: `node-${ip}`, type: 'otDevice', position: pos,
        data: {
          ip, name: knownDeviceName(ip, kd.name), role: deviceTypeLabel(knownDeviceName(ip, kd.name), kd.vendor, kd.role),
          roleClass: deviceRoleClass(knownDeviceName(ip, kd.name), kd.vendor, kd.role), riskLevel: 'normal', protocols: [], isInactive: true,
        },
      });
    });
  }

  // Active / involved device nodes
  ipList.forEach(ip => {
    const pos = ZONE_POSITIONS[ip] ||
      (hasFleetAssets && allFleetIps.includes(ip)
        ? fleetAssetPosition(allFleetIps, ip)
        : externalPosition(extIdx++));
    if (ip === EDGE_AGENT_IP) {
      nodes.push({
        id: `node-${ip}`, type: 'edgeAgent', position: pos,
        data: {
          hostname: edgeNode ? edgeNodeDisplayName(edgeNode, EDGE_AGENT_NAME) : EDGE_AGENT_NAME,
          policiesLoaded: edgeNode?.policies_loaded || 0,
          status: edgeNode?.status || 'Online',
        },
      });
    } else {
      const kd = KNOWN_DEVICES[ip];
      const liveAsset = assetsByIp && assetsByIp[ip];
      const isExternal = externalIps.includes(ip);
      const isCurrentTarget = activeStep?.destIp === ip;
      const extRole = isExternal ? inferExternalRole(ip, steps || [], assetsByIp) : null;
      const lastOctet = ip.split('.').slice(-1)[0];
      const typeName = liveAsset?.asset_name || knownDeviceName(ip, kd?.name);
      const typeVendor = liveAsset?.vendor || kd?.vendor;
      const storedRole = liveAsset?.asset_type || kd?.role || extRole || 'Unknown';
      nodes.push({
        id: `node-${ip}`, type: 'otDevice', position: pos,
        data: {
          ip,
          name: deviceNodeName(ip, assetsByIp) || (isExternal ? `${extRole} · .${lastOctet}` : ip),
          role: deviceTypeLabel(typeName, typeVendor, storedRole),
          roleClass: deviceRoleClass(typeName, typeVendor, storedRole),
          riskLevel: isCurrentTarget
            ? (activeStep?.hasCritical ? 'critical' : 'high')
            : (extRole === 'Candidate Origin' || extRole === 'Rogue Internal') ? 'high' : 'normal',
          protocols: [],
          isInactive: extRole === 'Out-of-Scope',
        },
      });
    }
  });

  (steps || []).slice(0, currentStep + 1).forEach((step, idx) => {
    const srcId = `node-${step.sourceIp}`;
    const dstId = `node-${step.destIp}`;
    const isActive = idx === currentStep;
    const isPast   = idx < currentStep;
    const proto = normalizeProtocol(step.protocol);
    const color = PROTOCOL_COLORS[proto] || '#475569';

    if (!step.sourceIp || !step.destIp) return;
    if (!nodes.find(n => n.id === srcId) || !nodes.find(n => n.id === dstId)) return;

    edges.push({
      id: `step-${idx}`,
      source: srcId, target: dstId,
      animated: isActive,
      style: {
        stroke: isActive ? color : `${color}44`,
        strokeWidth: isActive ? 4 : 1.5,
        filter: isActive ? `drop-shadow(0 0 5px ${color})` : undefined,
        strokeDasharray: step.hasEnforcement ? '4 2' : undefined,
        opacity: isPast ? 0.35 : 1,
      },
      label: isActive
        ? `${proto.toUpperCase()}${step.policies[0] ? ` · ${step.policies[0]}` : ''}`
        : proto.toUpperCase(),
      labelStyle: { fill: isActive ? color : `${color}66`, fontSize: isActive ? 10 : 9, fontWeight: isActive ? 700 : 400 },
      labelBgStyle: { fill: isActive ? 'rgba(255,255,255,0.95)' : 'transparent' },
    });
  });

  return { nodes, edges };
}

// ─── component ───────────────────────────────────────────────────────────────
export default function TopologyAttackChain({ events, assets, edgeNode, windowHours = 2, eventTotal = null, setWindowHours, onRefresh }) {
  const allSteps    = useMemo(() => buildAttackSteps(events), [events]);
  const traceCounts = useMemo(
    () => computeCounts({ events, hoursBack: windowHours, total: eventTotal }), [events, windowHours, eventTotal]);
  const [hidePolling, setHidePolling] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying]   = useState(false);
  const [speed, setSpeed]           = useState('1x');
  const [resetConfirm, setResetConfirm] = useState(false);
  const [showPolicies, setShowPolicies] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const playRef    = useRef(null);
  const stepListRef = useRef(null);
  const rfInstanceRef = useRef(null);

  const assetsByIp = useMemo(() => {
    const map = {};
    (assets || []).forEach(a => { if (a.ip_address) map[a.ip_address] = a; });
    return map;
  }, [assets]);

  const displaySteps = useMemo(
    () => hidePolling ? allSteps.filter(s => !s.isPeriodicPoll) : allSteps,
    [allSteps, hidePolling]
  );

  const pollCount = useMemo(
    () => allSteps.filter(s => s.isPeriodicPoll).length,
    [allSteps]
  );

  const pollingGroups = useMemo(() => {
    if (!hidePolling || pollCount === 0) return [];
    const polls = allSteps.filter(s => s.isPeriodicPoll);
    const byKey = {};
    polls.forEach(s => {
      const key = `${s.destIp}::${normalizeProtocol(s.protocol)}`;
      if (!byKey[key]) byKey[key] = {
        dest: nodeName(s.destIp, assetsByIp) || s.destIp || '—',
        proto: normalizeProtocol(s.protocol).toUpperCase(),
        steps: [],
      };
      byKey[key].steps.push(s);
    });
    return Object.values(byKey).map(g => {
      let avgMin = null;
      if (g.steps.length >= 2) {
        const ivs = [];
        for (let i = 1; i < g.steps.length; i++)
          ivs.push((new Date(g.steps[i].timestamp) - new Date(g.steps[i - 1].timestamp)) / 60000);
        avgMin = Math.round(ivs.reduce((a, b) => a + b, 0) / ivs.length);
      }
      return { dest: g.dest, proto: g.proto, count: g.steps.length, avgMin };
    });
  }, [allSteps, hidePolling, pollCount, assetsByIp]);

  // Policy fire aggregations — sorted critical-first, then by count
  const policyStats = useMemo(() => {
    const map = {};
    displaySteps.forEach(step => {
      step.events.forEach(ev => {
        const key = ev.policy_name || ev.rule_name;
        if (!key) return;
        if (!map[key]) {
          map[key] = { name: key, severity: ev.severity || 'Unknown', count: 0, lastFire: ev.timestamp,
            declaredAction: ev.details?.action_on_violation, actions: [] };
        }
        map[key].count++;
        map[key].actions.push(eventAction(ev));
        if (ev.timestamp > map[key].lastFire) map[key].lastFire = ev.timestamp;
      });
    });
    const SEV = { critical: 0, high: 1, warning: 2, medium: 3, low: 4 };
    return Object.values(map).sort((a, b) => {
      const sa = SEV[(a.severity || '').toLowerCase()] ?? 5;
      const sb = SEV[(b.severity || '').toLowerCase()] ?? 5;
      return sa !== sb ? sa - sb : b.count - a.count;
    });
  }, [displaySteps]);

  // Clamp step index when filter changes
  useEffect(() => {
    setCurrentStep(prev => Math.min(prev, Math.max(0, displaySteps.length - 1)));
  }, [displaySteps.length]);

  // Auto-fit viewport once when steps first arrive (data loaded after mount)
  const didFitRef = useRef(false);
  useEffect(() => {
    if (!didFitRef.current && displaySteps.length > 0 && rfInstanceRef.current) {
      didFitRef.current = true;
      setTimeout(() => rfInstanceRef.current?.fitView({ padding: 0.15, duration: 400 }), 60);
    }
  }, [displaySteps.length]);

  const { nodes: rfNodes, edges: rfEdges } = useMemo(
    () => buildAttackGraph(displaySteps, currentStep, edgeNode, assetsByIp),
    [displaySteps, currentStep, edgeNode, assetsByIp]
  );

  // Auto-scroll
  useEffect(() => {
    if (stepListRef.current) {
      const el = stepListRef.current.querySelector('[data-active="true"]');
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [currentStep]);

  // Play timer
  useEffect(() => {
    if (isPlaying) {
      playRef.current = setInterval(() => {
        setCurrentStep(prev => {
          if (prev >= displaySteps.length - 1) { setIsPlaying(false); return prev; }
          return prev + 1;
        });
      }, SPEED_DELAYS[speed]);
    }
    return () => clearInterval(playRef.current);
  }, [isPlaying, speed, displaySteps.length]);

  // Operator Summary — computed here from THIS site's steps in THIS window, and recomputed whenever
  // they change (site switch, window change, poll). It used to be fetched once from POST /chat/ and
  // cleared only on a window change, so a site switch kept the first site's text. /chat/ is not used:
  // it answers JSON while this page read a stream (so its text never showed), and its context is not
  // filtered by site, so its reply could name another site's devices.
  const operatorSummary = useMemo(
    () => (events.length === 0 ? '' : buildDeterministicSummary(allSteps.filter(s => !s.isPeriodicPoll), events, assetsByIp, edgeNode?.edge_node_id)),
    [allSteps, events, assetsByIp, edgeNode]
  );

  // One index for the list highlight, the step detail header and the replay counter.
  const displayStep = displaySteps[currentStep];

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-1 min-h-0">

        {/* ── Left rail — step list ── */}
        <div className="w-64 flex-shrink-0 flex flex-col border-r border-slate-200 bg-white/50" ref={stepListRef}>
          {/* Header + toggle */}
          <div className="p-3 border-b border-slate-200 space-y-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-semibold text-slate-700">Trace Steps</div>
              {onRefresh && (
                resetConfirm ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={async () => {
                        const base = process.env.REACT_APP_API_URL || '';
                        await fetch(`${base}/events/purge`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
                        setResetConfirm(false); onRefresh();
                      }}
                      className="text-[9px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-300 hover:bg-red-50">
                      Clear all
                    </button>
                    <button
                      onClick={async () => {
                        const base = process.env.REACT_APP_API_URL || '';
                        await fetch(`${base}/events/purge?older_than_hours=1`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
                        setResetConfirm(false); onRefresh();
                      }}
                      className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-50">
                      {'> 1h old'}
                    </button>
                    <button onClick={() => setResetConfirm(false)}
                      className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 hover:text-slate-700">
                      ✕
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setResetConfirm(true)}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 text-slate-600 hover:text-red-700 hover:border-red-300 transition-colors">
                    Reset
                  </button>
                )
              )}
            </div>

            {/* Time window selector. The button is the REQUESTED range; when the row limit binds, the
                trace covers only the last EVENT_SAMPLE_LIMIT events of it, and the selector says so
                (count-definitions.md: a time-range label is never shown for a capped set). */}
            {setWindowHours && (
              <div data-testid="trace-window-selector">
                <div className="flex items-center gap-0.5">
                  {WINDOW_OPTIONS.map(opt => {
                    const selected = windowHours === opt.value;
                    const capped = selected && traceCounts.window.capped;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setWindowHours(opt.value)}
                        aria-pressed={selected}
                        data-capped={capped ? 'true' : undefined}
                        title={capped ? `${opt.label} requested; showing only the last ${traceCounts.window.limit} events` : undefined}
                        className={`flex-1 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                          capped
                            ? 'text-amber-700 bg-amber-50 border-amber-300 border-dashed'
                            : selected
                              ? 'text-cyan-700 bg-cyan-50 border-cyan-300'
                              : 'text-slate-500 bg-slate-100/60 border-slate-200 hover:text-slate-600'
                        }`}
                      >
                        {opt.label}{capped ? '*' : ''}
                      </button>
                    );
                  })}
                </div>
                {traceCounts.window.capped && (
                  <div data-testid="trace-window-capped" className="text-[10px] text-amber-700 mt-0.5">
                    * {formatHours(windowHours)} requested · capped at the last {traceCounts.window.limit} events
                  </div>
                )}
              </div>
            )}

            {/* Polling headline — the value proposition sentence */}
            {/* Events and alerts: utils/counts.js, the same definitions and window as /ai-sentry. */}
            {allSteps.length > 0 && (
              <div className="text-[11px] text-slate-600 leading-tight pb-1">
                <span data-testid="trace-count-events" className="text-slate-700 font-semibold">{traceCounts.eventsText}</span>
                {' · '}
                {/* Beside a true total, the row-set figures name their rows ("in latest 500:"). */}
                {traceCounts.rowsPrefix && <span data-testid="trace-count-rows">{traceCounts.rowsPrefix}</span>}
                <span data-testid="trace-count-alerts" className="text-red-600 font-semibold">{traceCounts.alerts}</span> alerts
                {' · '}
                <span data-testid="trace-count-findings" className="text-purple-700 font-semibold">{traceCounts.findings}</span> findings
                {pollCount > 0 && (
                  <>
                    {' · '}
                    <span className="text-cyan-700 font-semibold">{displaySteps.length}</span> trace steps
                    {' · '}
                    <span className="text-amber-600 font-semibold">{pollCount}</span> routine polls {hidePolling ? 'filtered' : 'shown'}
                  </>
                )}
                <div data-testid="trace-count-window" className="text-[10px] text-slate-500">{traceCounts.window.label}</div>
              </div>
            )}

            {pollCount > 0 && (
              <button
                onClick={() => setHidePolling(h => !h)}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-[11px] font-medium transition-colors border ${
                  hidePolling
                    ? 'bg-amber-50 border-amber-300 text-amber-700'
                    : 'bg-slate-100/60 border-slate-200 text-slate-600 hover:text-slate-700'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                  </svg>
                  {hidePolling ? `${pollCount} polling steps hidden` : `Show all (incl. ${pollCount} polling)`}
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded ${hidePolling ? 'bg-amber-50' : 'bg-slate-200'}`}>
                  {hidePolling ? 'ON' : 'OFF'}
                </span>
              </button>
            )}
          </div>

          {/* Step list */}
          {displaySteps.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <div className="text-slate-500 text-xs mb-2">No events in time window</div>
              <div className="text-[11px] text-slate-400">Run tests to generate activity</div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {hidePolling && pollingGroups.length > 0 && (
                <div className="px-3 py-2 border-b border-amber-300 bg-amber-50">
                  <div className="text-[10px] text-amber-600 font-medium mb-1">
                    {pollCount} routine polling step{pollCount !== 1 ? 's' : ''} suppressed
                  </div>
                  {pollingGroups.map((g, i) => (
                    <div key={i} className="text-[9px] text-slate-500 font-mono mt-0.5">
                      {g.count}× → {g.dest} · {g.proto}{g.avgMin ? ` · ~${g.avgMin}m` : ''}
                    </div>
                  ))}
                </div>
              )}
              {displaySteps.map((s, idx) => {
                const isActive   = idx === currentStep;
                const isPast     = idx < currentStep;
                const proto      = normalizeProtocol(s.protocol);
                const protoColor = PROTOCOL_COLORS[proto] || '#475569';
                const label      = stepLabel(s, assetsByIp);
                const isL2       = !s.sourceIp && !s.destIp && proto === 'goose';
                const isDefender = s.hasEnforcement;

                return (
                  <React.Fragment key={idx}>
                  {s.incidentBreak && (
                    <div className="px-3 py-1 border-b border-slate-200 flex items-center gap-2">
                      <div className="flex-1 h-px bg-slate-200/50" />
                      <span className="text-[9px] text-slate-500 font-medium tracking-wider">NEW INCIDENT</span>
                      <div className="flex-1 h-px bg-slate-200/50" />
                    </div>
                  )}
                  <button
                    key={idx}
                    data-active={isActive}
                    onClick={() => setCurrentStep(idx)}
                    className={`w-full text-left py-2 border-b border-slate-200 transition-colors
                      ${isDefender
                        ? 'pl-6 pr-3 border-l-0 border-r-2 border-r-cyan-500/70 bg-cyan-50 text-right'
                        : 'pl-3 pr-3 border-l-2 border-l-red-700/30'}
                      ${isActive
                        ? (isDefender ? 'bg-cyan-50 !border-r-cyan-400' : 'bg-cyan-50 !border-l-cyan-500')
                        : ''}
                      ${isPast && !isActive ? 'opacity-40 hover:opacity-70' : 'hover:bg-slate-100/40'}
                    `}
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      {isDefender ? (
                        <span className="text-[10px] font-bold text-cyan-700">🛡 RESPONSE</span>
                      ) : (
                        <span className="text-[10px] font-bold text-slate-600">STEP {idx + 1}</span>
                      )}
                      <div className="flex items-center gap-1">
                        {s.hasCritical && <div className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                        <span className="text-[9px] font-semibold px-1 rounded"
                          style={{ background: `${protoColor}22`, color: protoColor }}>
                          {proto.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className={`text-[11px] leading-tight ${
                      isL2 ? 'text-yellow-700/80 italic' :
                      isDefender ? 'text-cyan-700/80' : 'text-slate-600'
                    } font-mono`}>
                      {label}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {fmtUtc(s.timestamp)}
                    </div>
                  </button>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Center — attack graph ── */}
        <div className="flex-1 relative etl-canvas" style={{ background: '#F8FAFC' }}>
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={TOPOLOGY_NODE_TYPES}
            onInit={(instance) => { rfInstanceRef.current = instance; }}
            minZoom={0.25}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
          >
            <Controls className="!bg-slate-100 !border-slate-200" />
            <Background color="#CBD5E1" variant={BackgroundVariant.Dots} gap={20} size={1} />
          </ReactFlow>

          {/* Bottom overlay removed — step detail is in right rail */}

          {displaySteps.length === 0 && allSteps.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-slate-400 text-sm">No event trace data for this window</div>
            </div>
          )}

          {displaySteps.length === 0 && allSteps.length > 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-slate-500 text-sm mb-2">All {allSteps.length} steps are periodic polling</div>
                <button onClick={() => setHidePolling(false)}
                  className="text-[12px] text-amber-700 underline">Show polling steps</button>
              </div>
            </div>
          )}
        </div>

        {/* ── Right rail ── */}
        <div className="w-72 flex-shrink-0 flex flex-col border-l border-slate-200 bg-white/50 overflow-y-auto">

          {/* Active step — prominent top section */}
          <div className="p-4 border-b border-slate-200 flex-shrink-0">
            {displayStep ? (() => {
              const proto = normalizeProtocol(displayStep.protocol);
              const protoColor = PROTOCOL_COLORS[proto] || '#475569';
              return (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold text-slate-600 tracking-widest" data-testid="etl-step-detail-index">
                      STEP {currentStep + 1} / {displaySteps.length}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                      style={{ background: `${protoColor}22`, color: protoColor, border: `1px solid ${protoColor}44` }}>
                      {proto.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-[13px] font-semibold text-slate-900 leading-snug mb-1.5" data-testid="etl-step-detail-route">
                    {nodeName(displayStep.sourceIp, assetsByIp)
                      || knownSourceLabel(displayStep)
                      || (displayStep.isPeriodicPoll ? '[Polling]' : '[Internal Host]')}
                    <span className="text-slate-600 mx-1">→</span>
                    {nodeName(displayStep.destIp, assetsByIp)
                      || displayStep.destIp || '[No Dest]'}
                  </div>
                  <div className="text-[11px] text-slate-600 mb-2.5">
                    {displayStep.events.length} event{displayStep.events.length !== 1 ? 's' : ''}
                    {' · '}
                    {fmtUtc(displayStep.timestamp)}
                    {displayStep.sourceIp && (
                      <span className="ml-1.5 font-mono text-slate-500">{displayStep.sourceIp}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${
                      displayStep.hasCritical
                        ? 'bg-red-50 text-red-700 border-red-300'
                        : 'bg-amber-50 text-amber-700 border-amber-300'
                    }`}>
                      {displayStep.hasCritical ? 'Critical' : 'Medium'}
                    </span>
                    {displayStep.hasEnforcement && (() => {
                      const tier = displayStep.events[0]?.details?.autonomy_tier;
                      const tierLabel = displayStep.events[0]?.details?.tier_label;
                      return (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-cyan-50 text-cyan-700 border-cyan-300 font-semibold"
                          title={tier || 'Enforcement action'}>
                          🛡 {tierLabel || 'EVALUATION'}{tier ? ` · ${tier}` : ''}
                        </span>
                      );
                    })()}
                    {displayStep.policies.slice(0, 2).map(p => (
                      <span key={p} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 truncate max-w-[140px]"
                        title={p}>{plainPolicyName(p)}</span>
                    ))}
                    {displayStep.policies.length > 2 && (
                      <span className="text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                        +{displayStep.policies.length - 2} more
                      </span>
                    )}
                  </div>

                  {/* Action staged + policy ID */}
                  {(() => {
                    const ev0 = displayStep.events[0];
                    const action = ev0?.action || ev0?.details?.rule_action;
                    const policyId = ev0?.policy_id || ev0?.details?.policy_id;
                    return (action || policyId) ? (
                      <div className="mt-2 pt-2 border-t border-slate-200 space-y-0.5">
                        {action && (
                          <div className="text-[10px] text-slate-500">
                            Action: <span className={`font-semibold ${
                              action === 'block' || action === 'drop' ? 'text-red-700' :
                              action === 'alert' ? 'text-amber-700' : 'text-slate-600'
                            }`}>{action}</span>
                          </div>
                        )}
                        {policyId && (
                          <div className="flex items-center gap-1.5 text-[10px] text-slate-600 font-mono">
                            <span className="font-sans text-slate-500">Policy ID:</span>
                            <span className="truncate max-w-[140px]" title={policyId}>
                              {policyId.length > 16 ? `${policyId.slice(0, 8)}…${policyId.slice(-6)}` : policyId}
                            </span>
                            <button
                              onClick={() => navigator.clipboard.writeText(policyId)}
                              title="Copy policy ID"
                              className="flex-shrink-0 text-slate-500 hover:text-cyan-700 transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}

                  {/* Event details expander — the logged event row, NOT sealed compliance evidence */}
                  <div className="mt-2">
                    <button
                      className="text-[10px] text-cyan-700 hover:text-cyan-600 transition-colors"
                      onClick={() => setShowDetails(prev =>
                        prev === displayStep.stepNum ? null : displayStep.stepNum
                      )}
                    >
                      {showDetails === displayStep.stepNum ? '▲ hide event details' : '▼ show event details'}
                    </button>
                    {showDetails === displayStep.stepNum && (
                      <dl className="mt-1 text-[9px] text-slate-600 leading-tight bg-white/60 rounded p-1.5 overflow-auto max-h-40 border border-slate-200 space-y-0.5"
                        data-testid="etl-event-details">
                        {eventDetailFields(displayStep.events[0]).map(([label, value]) => (
                          <div key={label} className="flex gap-1.5">
                            <dt className="text-slate-500 flex-shrink-0">{label}:</dt>
                            <dd className="font-mono break-all">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                </>
              );
            })() : (
              <div className="text-center py-3">
                <div className="text-[11px] text-slate-500">— no step selected —</div>
                <div className="text-[10px] text-slate-400 mt-1">Select a step or press play</div>
              </div>
            )}
          </div>

          {/* AI Analysis */}
          <div className="p-4 flex-1 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
              <span className="text-xs font-semibold text-slate-700">Operator Summary</span>
              {hidePolling && pollCount > 0 && (
                <span className="text-[10px] text-amber-600 ml-auto">{pollCount} polls excluded</span>
              )}
            </div>
            {operatorSummary ? (
              <div className="text-[12px] text-slate-700 leading-relaxed whitespace-pre-wrap font-mono" data-testid="etl-operator-summary">{operatorSummary}</div>
            ) : (
              <div className="text-[12px] text-slate-500">No trace events to analyze.</div>
            )}

            {/* Expandable policy fire log */}
            {policyStats.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <button
                  onClick={() => setShowPolicies(p => !p)}
                  className="w-full flex items-center justify-between text-[11px] text-slate-600 hover:text-slate-700 transition-colors"
                >
                  <span>
                    {policyStats.reduce((s, p) => s + p.count, 0)} policy fires ·{' '}
                    <span className="text-slate-600">{policyStats.length} unique rule{policyStats.length !== 1 ? 's' : ''}</span>
                  </span>
                  <span className="text-slate-500 text-[9px]">{showPolicies ? '▲ hide' : '▼ expand'}</span>
                </button>
                {showPolicies && (
                  <div className="mt-2 space-y-1">
                    {policyStats.map((p, i) => (
                      <div key={i} className="text-[10px] rounded px-2 py-1.5 bg-slate-100/50 border border-slate-200">
                        <div className="flex items-start justify-between gap-1 mb-0.5">
                          <span className="text-slate-700 break-all leading-tight" title={p.name}>{plainPolicyName(p.name)}</span>
                          {showEnforcementOff({ name: p.name, declaredAction: p.declaredAction, ruleActions: p.actions }) && (
                            <EnforcementOffLabel className="ml-1" />
                          )}
                          <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                            <span className={`px-1 py-0.5 rounded text-[7px] font-bold uppercase ${
                              (p.severity || '').toLowerCase() === 'critical' ? 'bg-red-50 text-red-700' :
                              (p.severity || '').toLowerCase() === 'high'     ? 'bg-orange-50 text-orange-700' :
                              'bg-amber-50 text-amber-600'
                            }`}>
                              {(p.severity || 'med').slice(0, 4)}
                            </span>
                            <span className="text-slate-600 font-mono">×{p.count}</span>
                          </div>
                        </div>
                        <div className="text-slate-400 font-mono text-[9px]">
                          last: {fmtUtc(p.lastFire)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Timeline controls ── */}
      <div className="flex-shrink-0 border-t border-slate-200 bg-white/80 px-4 py-2 flex items-center gap-4">
        <div className="flex items-center gap-1">
          <button onClick={() => setCurrentStep(0)} disabled={displaySteps.length === 0}
            className="p-1 rounded text-slate-600 hover:text-slate-800 disabled:opacity-30">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 6h2v12H6zm3.5 6L20 18V6z" /></svg>
          </button>
          <button onClick={() => setCurrentStep(s => Math.max(0, s - 1))} disabled={displaySteps.length === 0}
            className="p-1 rounded text-slate-600 hover:text-slate-800 disabled:opacity-30">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 6h2v12H6zm3.5 6L16 16V8z" /></svg>
          </button>
          <button onClick={() => setIsPlaying(p => !p)} disabled={displaySteps.length === 0}
            className={`p-1.5 rounded-full transition-colors disabled:opacity-30 ${isPlaying ? 'bg-red-100 text-red-700' : 'bg-cyan-100 text-cyan-700'}`}>
            {isPlaying
              ? <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
              : <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>}
          </button>
          <button onClick={() => setCurrentStep(s => Math.min(displaySteps.length - 1, s + 1))} disabled={displaySteps.length === 0}
            className="p-1 rounded text-slate-600 hover:text-slate-800 disabled:opacity-30">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 18l8.5-6L6 6v12zm2-8.14L11.03 12 8 14.14V9.86zM16 6h2v12h-2z" /></svg>
          </button>
        </div>

        <div className="flex flex-col flex-1 gap-0.5">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider leading-none">
            Replay event trace · 4× speed
          </div>
          <input
            type="range"
            min={0} max={Math.max(displaySteps.length - 1, 0)} value={currentStep}
            onChange={e => { setCurrentStep(Number(e.target.value)); setIsPlaying(false); }}
            disabled={displaySteps.length === 0}
            className="accent-cyan-500"
          />
        </div>

        <div className="text-[11px] text-slate-600 font-mono w-24 text-right">
          {displaySteps.length > 0
            ? `${currentStep + 1}/${displaySteps.length} · ${allSteps.length} total`
            : '—'}
        </div>

        <div className="flex items-center gap-1">
          {Object.keys(SPEED_DELAYS).map(s => (
            <button key={s} onClick={() => setSpeed(s)}
              className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${speed === s ? 'bg-cyan-100 text-cyan-700' : 'text-slate-600 hover:text-slate-700'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── MITRE ATT&CK for ICS stages ─────────────────────────────────────────────
// Assigned by attackStages (./attackStageLight.js): the approved mapping, one pure function.

// A source's class as the graph labels it: a named device (registered or curated), else the role
// inferExternalRole gives it (Out-of-Scope, Rogue Internal, Uninventoried OT, Candidate Origin, Scanner,
// External Host). The summary counts sources with this so it never contradicts the graph.
function sourceClass(ip, steps, assetsByIp) {
  return deviceNodeName(ip, assetsByIp) ? 'known device' : inferExternalRole(ip, steps, assetsByIp);
}
const SOURCE_CLASS_ORDER = ['Candidate Origin', 'Scanner', 'External Host', 'Rogue Internal', 'Uninventoried OT', 'Out-of-Scope', 'known device'];

function buildDeterministicSummary(attackSteps, events, assetsByIp = {}, siteId = null) {
  if (attackSteps.length === 0) return 'No trace events in the selected time window.';

  const protocols   = [...new Set(attackSteps.map(s => normalizeProtocol(s.protocol)).filter(Boolean))];
  const allPolicies = [...new Set(attackSteps.flatMap(s => s.policies))];
  const enforced    = attackSteps.filter(s => s.hasEnforcement).length;
  const critCount   = attackSteps.filter(s => s.hasCritical).length;
  const sources     = [...new Set(attackSteps.map(s => s.sourceIp).filter(Boolean))];
  const targets     = [...new Set(attackSteps.map(s => s.destIp).filter(Boolean))];
  // Target names as the graph shows them: device name, else the graph's "<role> · .<octet>" label.
  const knownTargets = targets.map(ip =>
    deviceNodeName(ip, assetsByIp) || `${inferExternalRole(ip, attackSteps, assetsByIp)} · .${ip.split('.').pop()}`);

  const sourceCounts = {};
  sources.forEach(ip => {
    const c = sourceClass(ip, attackSteps, assetsByIp);
    sourceCounts[c] = (sourceCounts[c] || 0) + 1;
  });

  // MITRE ATT&CK for ICS stages. A step carries every stage its events map to, so it can count under several
  // stages; the edge agent's own traffic gets no stage and is counted apart. A stage line's protocols are those of
  // the events that produced the stage, not the step's first event.
  const stages = attackStages(attackSteps, { siteId, assetsByIp });
  const stepsWord = n => `${n} step${n !== 1 ? 's' : ''}`;
  const phaseLines = STAGE_ORDER
    .map(p => [p, stages.filter(st => st && st[p])])
    .filter(([, withStage]) => withStage.length > 0)
    .map(([p, withStage]) => {
      const protos = [...new Set(withStage.flatMap(st => st[p].map(e => normalizeProtocol(e.protocol).toUpperCase())))].join('/');
      return `  ${p}: ${stepsWord(withStage.length)} · ${protos}`;
    });
  const excluded = stages.filter(st => st === null).length;
  if (phaseLines.length > 0) phaseLines.unshift('  (steps containing each stage; one step can show under several)');
  if (excluded > 0) phaseLines.push(`  Not staged (the edge agent's own monitoring and replies to it): ${stepsWord(excluded)}`);
  phaseLines.push(`  Trace steps: ${attackSteps.length} total (${attackSteps.length - excluded} staged, ${excluded} not staged)`);

  const targetStr = knownTargets.length > 0
    ? knownTargets.slice(0, 4).join(', ') + (knownTargets.length > 4 ? ` +${knownTargets.length - 4} more` : '')
    : 'Unknown';
  const sourceStr = SOURCE_CLASS_ORDER
    .filter(c => sourceCounts[c])
    .map(c => (c === 'known device'
      ? `${sourceCounts[c]} known device${sourceCounts[c] !== 1 ? 's' : ''}`
      : `${sourceCounts[c]} ${c.toLowerCase()}`))
    .join(', ') || 'unknown';

  return [
    'TRACE STAGES',
    ...phaseLines,
    '',
    'ASSETS TARGETED',
    `  Sources: ${sourceStr}`,
    `  Targets: ${targetStr}`,
    '',
    'POLICY RESPONSE',
    `  ${allPolicies.length} polic${allPolicies.length !== 1 ? 'ies' : 'y'} triggered across ${protocols.map(p => p.toUpperCase()).join(', ')} traffic`,
    `  ${critCount} critical step${critCount !== 1 ? 's' : ''} detected`,
    '',
    'EVALUATION',
    enforced > 0
      ? `  ${enforced} action${enforced !== 1 ? 's' : ''} recorded — enforcement active`
      : '  Observes. Never acts.',
  ].join('\n');
}

// Named exports for component tests (WP5).
export { stepLabel, buildAttackSteps, buildDeterministicSummary, eventDetailFields, sourceClass, buildAttackGraph };
